/** * ********** Pure js TAB with wcag **********
 */

/**
 * Agenda Tabs Component
 * Initializes accessible dropdown functionality for agenda tabs
 */
export function agendaTabs() {
    const tabContainers = document.querySelectorAll('.agenda-tabs');

    if (tabContainers.length === 0) return;

    tabContainers.forEach(container => {
        initializeAgendaTabs(container);
        handleOutsideClick(container);
        handleStickyScroll(container);
    });
}

/**
 * Initialize agenda tabs functionality

 */
function initializeAgendaTabs(container) {
    const selectBtn = container.querySelector('.select__btn');
    const selectDropdown = container.querySelector('.select__dropdown');
    const selectOptions = container.querySelectorAll('.option');
    const contentItems = container.querySelectorAll('.agenda-tabs-content__item');

    if (!selectBtn || !selectDropdown || selectOptions.length === 0) return;

    // Set initial state - show first content item
    const initialDataType = selectBtn.getAttribute('data-type') || selectOptions[0].getAttribute('data-type');
    showContentItem(container, initialDataType);

    // Add ARIA attributes for accessibility
    setupAccessibility(selectBtn, selectDropdown, selectOptions);

    // Button click handler
    selectBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleDropdown(selectDropdown, selectBtn);
    });

    // Option click handlers
    selectOptions.forEach(option => {
        option.addEventListener('click', (e) => {
            e.stopPropagation();
            handleOptionSelection(option, container, selectBtn, selectDropdown);
        });

        // Keyboard navigation for options
        option.addEventListener('keydown', (e) => {
            handleOptionKeydown(e, option, selectOptions, container, selectBtn, selectDropdown);
        });
    });

    // Keyboard navigation for button
    selectBtn.addEventListener('keydown', (e) => {
        handleButtonKeydown(e, selectBtn, selectDropdown, selectOptions);
    });
}


function setupAccessibility(selectBtn, selectDropdown, selectOptions) {
    // Button attributes
    selectBtn.setAttribute('role', 'combobox');
    selectBtn.setAttribute('aria-haspopup', 'listbox');
    selectBtn.setAttribute('aria-expanded', 'false');
    selectBtn.setAttribute('tabindex', '0');
    selectBtn.setAttribute('aria-controls', 'agenda-select-listbox');

    // Dropdown attributes
    selectDropdown.setAttribute('role', 'listbox');
    selectDropdown.setAttribute('id', 'agenda-select-listbox');
    selectDropdown.setAttribute('aria-label', 'Select agenda day');

    // Option attributes
    selectOptions.forEach((option, index) => {
        option.setAttribute('role', 'option');
        option.setAttribute('tabindex', index === 0 ? '0' : '-1');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('id', `agenda-option-${index + 1}`);
    });

    // Set first option as selected
    if (selectOptions.length > 0) {
        const currentDataType = selectBtn.getAttribute('data-type');
        selectOptions.forEach(option => {
            if (option.getAttribute('data-type') === currentDataType) {
                option.setAttribute('aria-selected', 'true');
            }
        });
    }
}

function toggleDropdown(selectDropdown, selectBtn) {
    const isOpen = selectDropdown.classList.contains('toggle');
    
    if (isOpen) {
        selectDropdown.classList.remove('toggle');
        selectBtn.setAttribute('aria-expanded', 'false');
        selectBtn.setAttribute('tabindex', '0');
    } else {
        selectDropdown.classList.add('toggle');
        selectBtn.setAttribute('aria-expanded', 'true');
        
        // Focus first option when opening
        const firstOption = selectDropdown.querySelector('.option[tabindex="0"]') || 
                           selectDropdown.querySelector('.option');
        if (firstOption) {
            firstOption.focus();
        }
    }
}

function handleOptionSelection(option, container, selectBtn, selectDropdown) {
    const dataType = option.getAttribute('data-type');
    
    if (!dataType) return;

    // Update button content and data
    const optionText = option.querySelector('p')?.innerHTML || option.innerHTML;
    const buttonText = selectBtn.querySelector('p');
    if (buttonText) {
        buttonText.innerHTML = optionText;
    }
    selectBtn.setAttribute('data-type', dataType);

    // Update ARIA attributes
    const allOptions = selectDropdown.querySelectorAll('.option');
    allOptions.forEach(opt => {
        opt.setAttribute('aria-selected', opt === option ? 'true' : 'false');
        opt.setAttribute('tabindex', opt === option ? '0' : '-1');
    });

    // Close dropdown
    selectDropdown.classList.remove('toggle');
    selectBtn.setAttribute('aria-expanded', 'false');

    // Show corresponding content
    showContentItem(container, dataType);

    // Scroll to first agenda-card in the selected content
    scrollToFirstAgendaCard(container, dataType);

    // Return focus to button
    selectBtn.focus();
    selectBtn.setAttribute('tabindex', '0');
}

/**
 * Show content item based on data type
 * @param {HTMLElement} container - The tab container element
 * @param {string} dataType - The data type to show
 */
function showContentItem(container, dataType) {
    const contentItems = container.querySelectorAll('.agenda-tabs-content__item');
    
    contentItems.forEach(item => {
        const itemId = item.getAttribute('id');
        if (itemId === dataType) {
            item.style.display = 'flex';
            item.setAttribute('aria-hidden', 'false');
        } else {
            item.style.display = 'none';
            item.setAttribute('aria-hidden', 'true');
        }
    });
}

/**
 * Scroll to the first agenda-card in the selected content section
 * @param {HTMLElement} container - The tab container element
 * @param {string} dataType - The data type of the selected content
 */
function scrollToFirstAgendaCard(container, dataType) {
    // Find the content item that matches the dataType
    const contentItem = container.querySelector(`.agenda-tabs-content__item[id="${dataType}"]`);
    
    if (contentItem) {
        // Find the first agenda-card within the content item
        const firstAgendaCard = contentItem.querySelector('.agenda-card');
        
        if (firstAgendaCard) {
            // Get the sticky header element and its height
            const header = document.querySelector('.header');
            const headerHeight = header ? header.offsetHeight : 0;
            
            // Calculate the position to scroll to (top of the first agenda-card minus header height)
            const cardTop = firstAgendaCard.getBoundingClientRect().top + window.pageYOffset - headerHeight - 185;
            
            // Scroll to the top of the first agenda-card with smooth behavior, accounting for header
            window.scrollTo({
                top: cardTop,
                behavior: 'smooth'
            });
        }
    }
}



/**
 * Handle clicks outside the select dropdown
 * @param {HTMLElement} container - The tab container element
 */
function handleOutsideClick(container) {
    document.addEventListener('click', (e) => {
        if (!container.contains(e.target)) {
            const selectBtn = container.querySelector('.select__btn');
            const selectDropdown = container.querySelector('.select__dropdown');
            
            if (selectBtn && selectDropdown && selectDropdown.classList.contains('toggle')) {
                selectDropdown.classList.remove('toggle');
                selectBtn.setAttribute('aria-expanded', 'false');
            }
        }
    });
}

/**
 * Handle sticky scroll behavior for agenda tabs dropdown
 * Adds/removes 'agenda-tabs-unsticky' class based on scroll position
 * @param {HTMLElement} container - The tab container element
 */
function handleStickyScroll(container) {
    const dropdown = container.querySelector('.agenda-tabs-dropdown');
    if (!dropdown) return;

    // Get the inner wrapper container inside agenda-tabs
    const wrapper = container.querySelector('.agenda-tabs__wrapper');
    if (!wrapper) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    function updateStickyClass() {
        const currentScrollY = window.scrollY;
        const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';
        
        // Get wrapper container dimensions (the inner container inside agenda-tabs)
        const wrapperRect = wrapper.getBoundingClientRect();
        const wrapperTop = wrapperRect.top + window.scrollY;
        const wrapperBottom = wrapperTop + wrapperRect.height;
        
        // Get sticky top offset (185px on desktop, 150px on mobile)
        const stickyTopOffset = window.innerWidth <= 1365 ? 150 : 185;
        
        // Calculate when wrapper bottom is 120px from sticky position
        // When: wrapperBottom - (scrollY + stickyTopOffset) <= 120
        const distanceFromSticky = wrapperBottom - (currentScrollY + stickyTopOffset);
        
        if (scrollDirection === 'down' && distanceFromSticky <= 200) {
            // Scrolling down and reached 120px from end - add unsticky class
            dropdown.classList.add('agenda-tabs-unsticky');
        } else if (scrollDirection === 'up') {
            // Scrolling up - remove unsticky class
            dropdown.classList.remove('agenda-tabs-unsticky');
        }

        lastScrollY = currentScrollY;
        ticking = false;
    }

    function onScroll() {
        if (!ticking) {
            window.requestAnimationFrame(updateStickyClass);
            ticking = true;
        }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
}


